import {
    BadgeCheck,
    Award,
    FileText,
    Package,
    MapPin,
    Wallet,
    ShieldCheck,
} from 'lucide-react';

import { ReactComponent as ArchiproMark } from '@rocco/icons/svg/logo-new-black.svg';
import { cn } from '@rocco/utils/cn';
import type {
    TrustBadge,
    TrustBadgeIcon,
    TrustAssociations,
} from '../utils/professional-trust-signals';

const ICONS: Record<Exclude<TrustBadgeIcon, 'available'>, typeof BadgeCheck> = {
    verified: BadgeCheck,
    award: Award,
    specs: FileText,
    samples: Package,
    locations: MapPin,
};

const Detail = ({ text, muted }: { text: string; muted: string }) => (
    <span className={cn('font-normal', muted)}>· {text}</span>
);

/**
 * The professional's credential line under the metrics. The Verified Member
 * signal keeps the dark pill so it reads first; everything else (awards, what
 * the pro offers, reach) sits as a compact icon + text one-liner that wraps —
 * fitting more in than a row of chips. Self-hides when there are none.
 */
export const ProfessionalTrustBadges = ({
    badges,
    typicalJob,
    associations,
}: {
    badges: TrustBadge[];
    typicalJob?: string | null;
    associations?: TrustAssociations | null;
}) => {
    if (badges.length === 0 && !typicalJob && !associations) return null;

    const verified = badges.find((badge) => badge.icon === 'verified');
    const rest = badges.filter((badge) => badge.icon !== 'verified');

    return (
        <div className="mt-4 flex flex-col gap-3 md:mt-5">
            {verified ? (
                // A light pill carrying the ArchiPro logomark — makes it clear
                // this is ArchiPro-verified, and doesn't mimic the solid dark
                // enquiry CTA below it. White, not an off-white: the profile's
                // ground is #f5f5f7 and a near-grey pill disappeared into it.
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white py-[0.4375rem] pr-3.5 pl-3 font-sans text-[0.8125rem] leading-none font-medium text-[#181818]">
                    <ArchiproMark
                        aria-hidden="true"
                        className="h-[0.8125rem] w-auto shrink-0"
                    />
                    <span>{verified.label}</span>
                    {verified.detail ? (
                        <Detail text={verified.detail} muted="text-[#8a8a8a]" />
                    ) : null}
                </span>
            ) : null}

            {associations ? (
                <span className="flex items-center gap-1.5 font-sans text-sm text-[#181818]">
                    <ShieldCheck
                        className="size-[1.0625rem] shrink-0 text-[#6a6a6a]"
                        strokeWidth={1.75}
                    />
                    <span className="truncate font-medium">
                        {associations.label}
                    </span>
                </span>
            ) : null}

            {typicalJob ? (
                <span className="inline-flex items-center gap-1.5 font-sans text-sm text-[#181818]">
                    <Wallet
                        className="size-[1.0625rem] shrink-0 text-[#6a6a6a]"
                        strokeWidth={1.75}
                    />
                    <span className="text-[#6a6a6a]">Typical job</span>
                    <span className="font-medium">{typicalJob}</span>
                </span>
            ) : null}

            {rest.length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-sans text-sm text-[#181818]">
                    {rest.map((badge) => {
                        const Icon =
                            badge.icon === 'available'
                                ? null
                                : ICONS[badge.icon];
                        return (
                            <span
                                key={badge.id}
                                className="inline-flex items-center gap-1.5"
                            >
                                {Icon ? (
                                    <Icon
                                        className="size-[1.0625rem] shrink-0 text-[#6a6a6a]"
                                        strokeWidth={1.75}
                                    />
                                ) : (
                                    <span
                                        aria-hidden="true"
                                        className="size-2 shrink-0 rounded-full bg-[#3fae5a]"
                                    />
                                )}
                                <span>{badge.label}</span>
                                {badge.detail ? (
                                    <Detail
                                        text={badge.detail}
                                        muted="text-[#8a8a8a]"
                                    />
                                ) : null}
                            </span>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};
